/**
 * Loans & Mortgages Page
 * Manage loans with escrow breakdown and amortization schedules.
 * Each active loan auto-generates expense rows for interest, tax, and insurance.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Landmark,
  Plus,
  Search,
  Loader2,
  DollarSign,
  Percent,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Loan {
  id: string;
  businessProfileId: string | null;
  businessProfileName: string | null;
  assetId: string | null;
  assetName: string | null;
  name: string;
  lender: string | null;
  description: string | null;
  loanType: string;
  originalAmount: number;
  interestRate: number;
  termMonths: number;
  startDate: string;
  maturityDate: string | null;
  monthlyPayment: number;
  escrowTax: number;
  escrowInsurance: number;
  extraPrincipal: number;
  currentBalance: number | null;
  status: string;
  isInterestDeductible: boolean;
  isTaxEscrowDeductible: boolean;
  isInsuranceDeductible: boolean;
  isInterestOnly: boolean;
  interestOnlyEndDate: string | null;
  taxCategory: string | null;
  notes: string | null;
}

interface LoanOverview {
  totalCount: number;
  activeCount: number;
  totalBalance: number;
  totalMonthlyPayment: number;
  totalMonthlyTax: number;
  totalMonthlyInsurance: number;
  totalOriginalAmount: number;
}

interface AmortizationRow {
  paymentNumber: number;
  paymentDate: string;
  paymentAmount: number;
  principal: number;
  interest: number;
  extraPrincipal: number;
  remainingBalance: number;
  isInterestOnly: boolean;
}

interface BusinessProfile { id: string; name: string; }
interface Asset { id: string; name: string; }

const LOAN_TYPES = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto', label: 'Auto Loan' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'business', label: 'Business Loan' },
  { value: 'personal', label: 'Personal Loan' },
  { value: 'line_of_credit', label: 'Line of Credit' },
  { value: 'other', label: 'Other' },
];

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  paid_off: 'Paid Off',
  deferred: 'Deferred',
  default: 'Default',
};

const STATUS_VARIANTS: Record<string, 'success' | 'secondary' | 'warning' | 'destructive'> = {
  active: 'success',
  paid_off: 'secondary',
  deferred: 'warning',
  default: 'destructive',
};

export function LoansPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState<Loan | null>(null);
  const [expandedLoan, setExpandedLoan] = useState<string | null>(null);

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: assetsData } = useQuery({
    queryKey: ['assets', { status: 'active' }],
    queryFn: () => api.getAssets({ status: 'active', limit: 100 }),
  });

  const { data: overviewData } = useQuery({
    queryKey: ['loanOverview', { businessProfileId: profileFilter }],
    queryFn: () => api.getLoanOverview({ businessProfileId: profileFilter || undefined }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['loans', { page, search, loanType: typeFilter, businessProfileId: profileFilter }],
    queryFn: () => api.getLoans({
      page, limit: 20,
      search: search || undefined,
      loanType: typeFilter || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const assets = ((assetsData?.data || []) as Asset[]);
  const overview = overviewData?.data as LoanOverview | undefined;
  const loans = (data?.data || []) as Loan[];
  const pagination = data?.pagination;

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createLoan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loanOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      setDialogOpen(false);
      toast({ title: 'Loan created' });
    },
    onError: () => toast({ title: 'Failed to create loan', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateLoan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loanOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      setDialogOpen(false);
      setEditingLoan(null);
      toast({ title: 'Loan updated' });
    },
    onError: () => toast({ title: 'Failed to update loan', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLoan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loanOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      toast({ title: 'Loan deleted' });
    },
    onError: () => toast({ title: 'Failed to delete loan', variant: 'destructive' }),
  });

  function openCreate() { setEditingLoan(null); setDialogOpen(true); }
  function openEdit(loan: Loan) { setEditingLoan(loan); setDialogOpen(true); }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      businessProfileId: fd.get('businessProfileId') || null,
      assetId: fd.get('assetId') || null,
      name: fd.get('name'),
      lender: fd.get('lender') || null,
      description: fd.get('description') || null,
      loanType: fd.get('loanType') || 'mortgage',
      originalAmount: parseFloat(fd.get('originalAmount') as string),
      interestRate: parseFloat(fd.get('interestRate') as string),
      termMonths: parseInt(fd.get('termMonths') as string, 10),
      startDate: fd.get('startDate'),
      maturityDate: fd.get('maturityDate') || null,
      monthlyPayment: parseFloat(fd.get('monthlyPayment') as string),
      escrowTax: parseFloat(fd.get('escrowTax') as string) || 0,
      escrowInsurance: parseFloat(fd.get('escrowInsurance') as string) || 0,
      extraPrincipal: parseFloat(fd.get('extraPrincipal') as string) || 0,
      currentBalance: fd.get('currentBalance') ? parseFloat(fd.get('currentBalance') as string) : null,
      status: fd.get('status') || 'active',
      isInterestDeductible: fd.get('isInterestDeductible') === 'on',
      isTaxEscrowDeductible: fd.get('isTaxEscrowDeductible') === 'on',
      isInsuranceDeductible: fd.get('isInsuranceDeductible') === 'on',
      isInterestOnly: fd.get('isInterestOnly') === 'on',
      interestOnlyEndDate: fd.get('interestOnlyEndDate') || null,
      notes: fd.get('notes') || null,
    };

    if (editingLoan) {
      updateMutation.mutate({ id: editingLoan.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const totalMonthly = overview
    ? overview.totalMonthlyPayment
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Loan
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Balance</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalBalance || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><Calendar className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Payment</p>
                <p className="text-xl font-bold">{formatCurrency(totalMonthly)}</p>
                <p className="text-xs text-muted-foreground">P&I + escrow</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><Percent className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Escrow</p>
                <p className="text-xl font-bold">{formatCurrency((overview?.totalMonthlyTax || 0) + (overview?.totalMonthlyInsurance || 0))}</p>
                <p className="text-xs text-muted-foreground">
                  Tax: {formatCurrency(overview?.totalMonthlyTax || 0)} · Ins: {formatCurrency(overview?.totalMonthlyInsurance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10"><Landmark className="h-5 w-5 text-orange-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Active Loans</p>
                <p className="text-xl font-bold">{overview?.activeCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search loans..." className="pl-9" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {LOAN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All profiles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profiles</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Loan List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : loans.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Landmark className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No loans found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {loans.map((loan) => (
            <LoanCard
              key={loan.id}
              loan={loan}
              expanded={expandedLoan === loan.id}
              onToggle={() => setExpandedLoan(expandedLoan === loan.id ? null : loan.id)}
              onEdit={() => openEdit(loan)}
              onDelete={() => { if (confirm('Delete this loan and its linked expenses?')) deleteMutation.mutate(loan.id); }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{pagination.total} loan{pagination.total !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingLoan?.id || 'new'} className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLoan ? 'Edit Loan' : 'New Loan'}</DialogTitle>
            <DialogDescription>
              {editingLoan ? 'Update the loan details.' : 'Enter the loan details. Interest, tax, and insurance will auto-generate as expenses.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Loan Name *</Label>
                <Input name="name" id="name" required defaultValue={editingLoan?.name || ''} placeholder="e.g. Office Building Mortgage" />
              </div>
              <div>
                <Label htmlFor="lender">Lender</Label>
                <Input name="lender" id="lender" defaultValue={editingLoan?.lender || ''} placeholder="e.g. Wells Fargo" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="loanType">Loan Type</Label>
                <select name="loanType" id="loanType" defaultValue={editingLoan?.loanType || 'mortgage'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="businessProfileId">Business Profile</Label>
                <select name="businessProfileId" id="businessProfileId" defaultValue={editingLoan?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">None</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="assetId">Linked Asset</Label>
                <select name="assetId" id="assetId" defaultValue={editingLoan?.assetId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">None</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label htmlFor="originalAmount">Original Amount *</Label>
                <Input type="number" name="originalAmount" id="originalAmount" step="0.01" min="0" required
                  defaultValue={editingLoan?.originalAmount || ''} />
              </div>
              <div>
                <Label htmlFor="interestRate">Rate (%) *</Label>
                <Input type="number" name="interestRate" id="interestRate" step="0.0001" min="0" max="100" required
                  defaultValue={editingLoan?.interestRate || ''} />
              </div>
              <div>
                <Label htmlFor="termMonths">Term (months) *</Label>
                <Input type="number" name="termMonths" id="termMonths" min="1" required
                  defaultValue={editingLoan?.termMonths || ''} />
              </div>
              <div>
                <Label htmlFor="monthlyPayment">Monthly Payment *</Label>
                <Input type="number" name="monthlyPayment" id="monthlyPayment" step="0.01" min="0" required
                  defaultValue={editingLoan?.monthlyPayment || ''} />
                <p className="text-xs text-muted-foreground mt-0.5">Interest-only: enter interest payment</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" name="isInterestOnly" id="isInterestOnly" className="h-4 w-4"
                  defaultChecked={editingLoan?.isInterestOnly ?? false} />
                <Label htmlFor="isInterestOnly">Interest-Only Loan</Label>
              </div>
              <div>
                <Label htmlFor="interestOnlyEndDate">Interest-Only Period Ends</Label>
                <Input type="date" name="interestOnlyEndDate" id="interestOnlyEndDate"
                  defaultValue={editingLoan?.interestOnlyEndDate ? editingLoan.interestOnlyEndDate.split('T')[0] : ''} />
                <p className="text-xs text-muted-foreground mt-0.5">Leave blank if fully interest-only</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="escrowTax">Monthly Tax Escrow</Label>
                <Input type="number" name="escrowTax" id="escrowTax" step="0.01" min="0"
                  defaultValue={editingLoan?.escrowTax || '0'} />
              </div>
              <div>
                <Label htmlFor="escrowInsurance">Monthly Insurance Escrow</Label>
                <Input type="number" name="escrowInsurance" id="escrowInsurance" step="0.01" min="0"
                  defaultValue={editingLoan?.escrowInsurance || '0'} />
              </div>
              <div>
                <Label htmlFor="extraPrincipal">Extra Principal</Label>
                <Input type="number" name="extraPrincipal" id="extraPrincipal" step="0.01" min="0"
                  defaultValue={editingLoan?.extraPrincipal || '0'} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date *</Label>
                <Input type="date" name="startDate" id="startDate" required
                  defaultValue={editingLoan?.startDate ? editingLoan.startDate.split('T')[0] : ''} />
              </div>
              <div>
                <Label htmlFor="maturityDate">Maturity Date</Label>
                <Input type="date" name="maturityDate" id="maturityDate"
                  defaultValue={editingLoan?.maturityDate ? editingLoan.maturityDate.split('T')[0] : ''} />
              </div>
              <div>
                <Label htmlFor="currentBalance">Current Balance</Label>
                <Input type="number" name="currentBalance" id="currentBalance" step="0.01" min="0"
                  defaultValue={editingLoan?.currentBalance ?? ''} />
              </div>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <select name="status" id="status" defaultValue={editingLoan?.status || 'active'}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="active">Active</option>
                <option value="paid_off">Paid Off</option>
                <option value="deferred">Deferred</option>
              </select>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <input type="checkbox" name="isInterestDeductible" id="isInterestDeductible" className="h-4 w-4"
                  defaultChecked={editingLoan?.isInterestDeductible ?? true} />
                <Label htmlFor="isInterestDeductible">Interest Deductible</Label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="isTaxEscrowDeductible" id="isTaxEscrowDeductible" className="h-4 w-4"
                  defaultChecked={editingLoan?.isTaxEscrowDeductible ?? true} />
                <Label htmlFor="isTaxEscrowDeductible">Tax Escrow Deductible</Label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="isInsuranceDeductible" id="isInsuranceDeductible" className="h-4 w-4"
                  defaultChecked={editingLoan?.isInsuranceDeductible ?? false} />
                <Label htmlFor="isInsuranceDeductible">Insurance Deductible</Label>
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea name="description" id="description" rows={2} defaultValue={editingLoan?.description || ''} />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingLoan?.notes || ''} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingLoan ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// Loan Card with expandable amortization
// ============================================

function LoanCard({ loan, expanded, onToggle, onEdit, onDelete }: {
  loan: Loan;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const totalMonthly = loan.monthlyPayment + loan.escrowTax + loan.escrowInsurance;
  const monthlyInterest = loan.isInterestOnly
    ? loan.monthlyPayment
    : loan.currentBalance
      ? Math.round(loan.currentBalance * (loan.interestRate / 100) / 12 * 100) / 100
      : 0;
  const monthlyPrincipal = loan.isInterestOnly ? 0 : loan.monthlyPayment - monthlyInterest;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="font-semibold">{loan.name}</h3>
              <Badge variant={STATUS_VARIANTS[loan.status] || 'secondary'}>
                {STATUS_LABELS[loan.status] || loan.status}
              </Badge>
              <Badge variant="outline">
                {LOAN_TYPES.find(t => t.value === loan.loanType)?.label || loan.loanType}
              </Badge>
              {loan.isInterestOnly && (
                <Badge variant="warning">Interest-Only</Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground space-y-0.5">
              {loan.lender && <p>Lender: {loan.lender}</p>}
              {loan.businessProfileName && <p>Profile: {loan.businessProfileName}</p>}
              {loan.assetName && <p>Asset: {loan.assetName}</p>}
            </div>
          </div>
          <div className="text-right space-y-1">
            <p className="text-lg font-bold">{formatCurrency(totalMonthly)}<span className="text-sm text-muted-foreground font-normal">/mo</span></p>
            <p className="text-sm text-muted-foreground">Balance: {formatCurrency(loan.currentBalance || 0)}</p>
          </div>
        </div>

        {/* Monthly breakdown */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Principal</span>
            <p className="font-medium">{formatCurrency(Math.max(monthlyPrincipal, 0))}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Interest</span>
            <p className="font-medium">{formatCurrency(monthlyInterest)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Tax Escrow</span>
            <p className="font-medium">{formatCurrency(loan.escrowTax)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Insurance</span>
            <p className="font-medium">{formatCurrency(loan.escrowInsurance)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Rate / Term</span>
            <p className="font-medium">{loan.interestRate}% / {Math.round(loan.termMonths / 12)}yr</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-3 flex items-center gap-2 border-t pt-3">
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {expanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
            Amortization
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={onEdit}>Edit</Button>
          <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete}>Delete</Button>
        </div>

        {/* Amortization Schedule */}
        {expanded && <AmortizationSchedule loanId={loan.id} />}
      </CardContent>
    </Card>
  );
}

function AmortizationSchedule({ loanId }: { loanId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['loanAmortization', loanId],
    queryFn: () => api.getLoanAmortization(loanId),
  });

  const schedule = (data?.data || []) as AmortizationRow[];

  if (isLoading) return <div className="py-4 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (schedule.length === 0) return <p className="text-sm text-muted-foreground py-4 text-center">No amortization data</p>;

  // Find current payment (closest to today)
  const today = new Date().toISOString().split('T')[0];
  const currentPaymentIdx = schedule.findIndex(r => r.paymentDate >= today);

  return (
    <div className="mt-3 border-t pt-3 max-h-80 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-background">
          <tr className="border-b text-muted-foreground">
            <th className="text-left p-2">#</th>
            <th className="text-left p-2">Date</th>
            <th className="text-right p-2">Payment</th>
            <th className="text-right p-2">Principal</th>
            <th className="text-right p-2">Interest</th>
            <th className="text-right p-2">Balance</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((row, idx) => (
            <tr key={row.paymentNumber}
              className={idx === currentPaymentIdx ? 'bg-primary/5 font-medium' : row.isInterestOnly ? 'bg-amber-500/5 hover:bg-amber-500/10' : 'hover:bg-muted/30'}>
              <td className="p-2">
                {row.paymentNumber}
                {row.isInterestOnly && <span className="text-[10px] text-amber-600 ml-1">IO</span>}
              </td>
              <td className="p-2">{formatDate(row.paymentDate)}</td>
              <td className="p-2 text-right">{formatCurrency(row.paymentAmount)}</td>
              <td className="p-2 text-right">{row.isInterestOnly ? <span className="text-muted-foreground">-</span> : formatCurrency(row.principal)}</td>
              <td className="p-2 text-right">{formatCurrency(row.interest)}</td>
              <td className="p-2 text-right">{formatCurrency(row.remainingBalance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
